import {FC, useCallback, useEffect, useState} from "react";
import BaseModal from "@memaro/components/Modal.tsx";
import apiClient from "../../../lib/api.ts";
import {GroupMembership} from "@memaro/api";
import Table from "./Table.tsx";
import {useFeedback} from "@memaro/feedback";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";

export type ModalProps = {
    open: boolean;
    profileID: string;
    onClose: () => void;
}

const Modal: FC<ModalProps> = ({open, profileID, onClose}) => {
    const [memberships, setMemberships] = useState<GroupMembership[]>([])
    const [creating, setCreating] = useState<boolean>(false)
    const [updating, setUpdating] = useState<GroupMembership | undefined>(undefined)
    const [deleting, setDeleting] = useState<GroupMembership | undefined>(undefined)
    const [showNewRow, setShowNewRow] = useState<boolean>(false)
    const {error} = useFeedback()

    const handleAdd = useCallback(() => setShowNewRow(true), [])
    useEffect(() => {
        if (!creating || !open) { // when "creating" transitions from true to false, hide the new row
            setShowNewRow(false);
        }
    }, [creating, open]);

    const loadGroupMemberships = useCallback(() => {
        apiClient.listGroupMembership(profileID)
            .then(setMemberships)
            .catch(() => error?.("Er is een fout opgetreden bij het ophalen van de personen met toegang"))
    }, [profileID, error])

    useEffect(() => {
        if (open) {
            loadGroupMemberships()
        }
    }, [open, loadGroupMemberships])

    const handleCreate = useCallback((gm: GroupMembership) => {
        setCreating(true)
        apiClient.saveGroupMembership(profileID, gm)
            .then(loadGroupMemberships)
            .catch(() => error?.("Er is een fout opgetreden bij het opslaan van de toegang"))
            .finally(() => setCreating(false))
    }, [error, loadGroupMemberships, profileID])

    const handleUpdate = useCallback((gm: GroupMembership) => {
        setUpdating(gm)
        apiClient.saveGroupMembership(profileID, gm)
            .then(loadGroupMemberships)
            .catch(() => error?.("Er is een fout opgetreden bij het opslaan van de toegang"))
            .finally(() => setUpdating(undefined))
    }, [error, loadGroupMemberships, profileID]);

    const handleDelete = useCallback((gm: GroupMembership) => {
        setDeleting(gm)
        apiClient.deleteGroupMembership(profileID, gm.username)
            .then(loadGroupMemberships)
            .catch(() => error?.("Er is een fout opgetreden bij het ontzeggen van de toegang"))
            .finally(() => setDeleting(undefined))
    }, [error, loadGroupMemberships, profileID]);

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            title="Toegang beheren"
            fullScreenOnMobile
            actions={[
                <Button key="add" onClick={handleAdd} disabled={showNewRow}>
                    <AddIcon/>
                    Toevoegen
                </Button>,
                <Button key="close" onClick={onClose}>
                    Sluiten
                </Button>
            ]}>
            <Table
                rows={memberships}
                onCreate={handleCreate}
                creating={creating}
                onUpdate={handleUpdate}
                updating={updating}
                onDelete={handleDelete}
                deleting={deleting}
                showNewRow={showNewRow}
            />
        </BaseModal>
    )
}

export default Modal
